import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export default function Login() {
  const { user, loading, signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    if (mode === 'signin') {
      const res = await signIn(email, password);
      if (res.error) setError(res.error);
    } else {
      const res = await signUp(email, password);
      if (res.error) {
        setError(res.error);
      } else if (!res.session) {
        setSuccess('Kayıt isteği alındı. Lütfen e-posta adresinize gönderilen onay linkine tıklayın.');
        setMode('signin');
      }
    }
    setBusy(false);
  };

  const switchMode = () => {
    setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-foreground-950 px-4">
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent-500/15 blur-3xl" />

      <div className="relative z-10 w-full max-w-md animate-fade-in-up">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-500 font-heading text-xl font-bold text-white">
            3D
          </div>
          <h1 className="mt-4 font-heading text-2xl font-semibold text-white">3D Machine</h1>
          <p className="mt-1 text-sm text-foreground-300">İş Yönetim Paneli</p>
        </div>

        <div className="rounded-xl border border-foreground-800 bg-foreground-900 p-6">
          <h2 className="font-heading text-lg font-semibold text-white">
            {mode === 'signin' ? 'Giriş Yap' : 'Hesap Oluştur'}
          </h2>
          <p className="mt-1 text-sm text-foreground-400">
            {mode === 'signin'
              ? 'Panele erişmek için hesabınızla giriş yapın.'
              : 'İlk kurulum için yönetici hesabınızı oluşturun.'}
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground-200">E-posta</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                className="w-full rounded-md border border-foreground-700 bg-foreground-950 px-3 py-2.5 text-sm text-white placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground-200">Şifre</span>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-md border border-foreground-700 bg-foreground-950 px-3 py-2.5 text-sm text-white placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30"
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-accent-500/15 px-3 py-2.5 text-sm text-accent-200">
                <i className="ri-error-warning-line mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="flex items-start gap-2 rounded-md bg-primary-500/15 px-3 py-2.5 text-sm text-primary-200">
                <i className="ri-check-double-line mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy && <i className="ri-loader-4-line animate-spin" />}
              {mode === 'signin' ? 'Giriş Yap' : 'Hesap Oluştur'}
            </button>
          </form>

          <button
            type="button"
            onClick={switchMode}
            className="mt-4 w-full cursor-pointer text-center text-sm text-foreground-400 transition-colors hover:text-white"
          >
            {mode === 'signin' ? 'Hesabınız yok mu? Kayıt olun' : 'Zaten hesabınız var mı? Giriş yapın'}
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-foreground-500">
          3D Machine Tic. Ltd. Şti. · Endüstriyel Otomasyon Çözümleri
        </p>
      </div>
    </div>
  );
}